import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Crown, 
  MessageSquare, 
  Zap,
  HelpCircle,
  HeartCrack,
  AlertTriangle,
  Loader2,
  Sparkles,
  Heart,
  Lightbulb,
  ArrowRight,
  Lock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const triggers = [
  { 
    id: 'text_him', 
    label: 'I want to text him', 
    icon: MessageSquare,
    description: 'The urge to reach out is strong',
    color: 'from-rose-400 to-rose-600'
  },
  { 
    id: 'triggered', 
    label: 'I feel triggered', 
    icon: Zap,
    description: 'Something set off my anxiety',
    color: 'from-orange-400 to-orange-600'
  },
  { 
    id: 'doubt_self', 
    label: 'I\'m doubting myself', 
    icon: HelpCircle,
    description: 'Was I the problem?',
    color: 'from-purple-400 to-purple-600'
  },
  { 
    id: 'miss_him', 
    label: 'I miss him', 
    icon: HeartCrack,
    description: 'Missing the good times',
    color: 'from-pink-400 to-pink-600'
  },
  { 
    id: 'overreacted', 
    label: 'Did I overreact?', 
    icon: AlertTriangle,
    description: 'Questioning my response',
    color: 'from-amber-400 to-amber-600'
  },
];

export default function AdjustMyCrown() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [context, setContext] = useState('');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);

  // Premium gate
  if (!user?.is_premium) {
    return (
      <Layout>
        <div className="p-6 lg:p-10 max-w-2xl mx-auto text-center" data-testid="adjust-crown-page">
          <div className="crown-card p-10">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-muted mb-6">
              <Lock className="w-10 h-10 text-muted-foreground" />
            </div>
            <h1 className="font-serif text-2xl font-bold text-foreground mb-3">
              Premium Feature
            </h1>
            <p className="text-muted-foreground mb-6">
              The AI-powered "Adjust My Crown" tool is available with Crown Premium. 
              Get instant, Christ-centered support when you're struggling.
            </p>
            <Button 
              onClick={() => navigate('/subscription')}
              className="btn-gold rounded-full px-8 py-5"
            >
              <Crown className="w-5 h-5 mr-2" />
              Upgrade to Premium
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const handleAdjustCrown = async () => {
    if (!selectedTrigger) {
      toast.error('Please select what you\'re feeling');
      return;
    }

    setLoading(true);
    setResponse(null);

    try {
      const res = await axios.post(`${API}/crown/adjust`, {
        trigger_type: selectedTrigger,
        context: context || null
      });
      setResponse(res.data);
      toast.success('Your crown is adjusted, Queen!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedTrigger(null);
    setContext('');
    setResponse(null);
  };

  return (
    <Layout>
      <div className="p-6 lg:p-10 max-w-4xl mx-auto" data-testid="adjust-crown-page">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full gold-gradient mb-6 animate-pulse-gold">
            <Crown className="w-10 h-10 text-purple-950" />
          </div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-foreground mb-3">
            Adjust My Crown
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            When you're struggling, tap into Christ-centered truth. 
            Select what you're feeling to receive personalized support.
          </p>
        </div>

        {!response ? (
          <>
            {/* Trigger Selection */}
            <div className="mb-8">
              <h2 className="font-semibold text-foreground mb-4">What are you feeling?</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {triggers.map((trigger) => {
                  const Icon = trigger.icon;
                  const isSelected = selectedTrigger === trigger.id;
                  return (
                    <button
                      key={trigger.id}
                      onClick={() => setSelectedTrigger(trigger.id)}
                      data-testid={`trigger-${trigger.id}`}
                      className={`crown-card p-5 text-left transition-all duration-300 hover:-translate-y-1 ${
                        isSelected 
                          ? 'ring-2 ring-primary border-primary/50' 
                          : 'hover:border-primary/30'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${trigger.color} flex items-center justify-center mb-3`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-semibold text-foreground mb-1">{trigger.label}</h3>
                      <p className="text-sm text-muted-foreground">{trigger.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Optional Context */}
            <div className="mb-8">
              <h2 className="font-semibold text-foreground mb-2">
                Want to share more? <span className="text-muted-foreground font-normal">(Optional)</span>
              </h2>
              <Textarea
                data-testid="context-input"
                placeholder="Tell me what's going on... (This helps personalize your support)"
                value={context}
                onChange={(e) => setContext(e.target.value)}
                className="min-h-[120px] resize-none"
              />
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <Button
                onClick={handleAdjustCrown}
                disabled={!selectedTrigger || loading}
                data-testid="adjust-crown-btn"
                className="btn-gold rounded-full px-10 py-6 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Adjusting Your Crown...
                  </>
                ) : (
                  <>
                    <Crown className="w-5 h-5 mr-2" />
                    Adjust My Crown
                  </>
                )}
              </Button>
            </div>
          </>
        ) : (
          /* Response Display */
          <div className="space-y-6 animate-fade-in" data-testid="crown-response">
            {/* Truth */}
            <div className="glass-gold rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="font-semibold text-amber-700 dark:text-amber-300">Truth About Manipulation</h3>
              </div>
              <p className="text-foreground leading-relaxed">{response.truth}</p>
            </div>

            {/* Identity Reminder */}
            <div className="crown-card p-6 border-purple-200 dark:border-purple-700/50">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="font-semibold text-purple-600">Who You Are in Christ</h3>
              </div>
              <p className="font-scripture text-lg italic text-foreground leading-relaxed">{response.identity_reminder}</p>
            </div>

            {/* Loving Correction */}
            <div className="crown-card p-6 border-rose-200 dark:border-rose-700/50">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-rose-500/20 flex items-center justify-center">
                  <Heart className="w-5 h-5 text-rose-600" />
                </div>
                <h3 className="font-semibold text-rose-600">Loving Correction</h3>
              </div>
              <p className="text-foreground font-medium leading-relaxed">{response.loving_correction}</p>
            </div>

            {/* Action Step */}
            <div className="royal-gradient rounded-2xl p-6 text-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <ArrowRight className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold">Your Action Step</h3>
              </div>
              <p className="leading-relaxed">{response.action_step}</p>
            </div>

            {/* Reset Button */}
            <div className="text-center pt-4">
              <Button
                onClick={resetForm}
                variant="outline"
                data-testid="reset-btn"
                className="rounded-full px-8 py-5 border-2 border-primary text-primary hover:bg-primary/5"
              >
                Need Another Adjustment?
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
